import { Dialog, DialogActions, DialogContent, DialogTitle } from "@mui/material";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { ModalLoading } from "../../../../../../common/components/modal_loading";
import FormContainer from "../../../../../../common/components/form_container";
import PrimaryButton from "../../../../../../common/components/primary_button";
import { defaultReportRateioGeralForm, ReportRateioGeralForm, ReportRateioGeralSchema } from "../../../schemas/reports/rateioGeralReport";
import { useCallback } from "react";
import { CentroCustoDropDown } from "../../../../../../common/domain/models/centro_custo_dropdown";
import { DropDown } from "../../../../../../common/domain/models/dropdown";
import { DefaultFormMultiAutocomplete } from "../../../../../../common/components/default_form_multi_autocomplete";
import { FormDatePickerInput } from "../../../../../../common/components/masked_inputs/react_forms/form_date_picker_input";
import DefaultFormInput from "../../../../../../common/components/default_form_input";
import { DefaultFormMoneyInput } from "../../../../../../common/components/default_form_money_input";

type props = {
    open: boolean,
    saving: boolean,
    ddlCentroCusto: CentroCustoDropDown[];
    ddlFuncionario: DropDown[];
    ddlFornecedor: DropDown[];
    ddlOrdemCompra: DropDown[];
    ddlNroMedicao: DropDown[];

    onClose: () => void,
    onSubmit: (form: ReportRateioGeralForm) => void,

}

export const RateioGeralReportModal = ({
    open,
    saving,
    ddlCentroCusto,
    ddlFuncionario,
    ddlFornecedor,
    ddlOrdemCompra,
    ddlNroMedicao,
    onClose,
    onSubmit,

}: props) => {

    const hookForm = useForm<ReportRateioGeralForm>({
        mode: "onSubmit",
        reValidateMode: "onSubmit",
        defaultValues: defaultReportRateioGeralForm,
        resolver: zodResolver(ReportRateioGeralSchema),
    });


    const handleSubmit = useCallback((form: ReportRateioGeralForm) => {
        if (!saving) {
            onSubmit(form);
            hookForm.reset();

        }
    }, [saving, onSubmit, hookForm]);

    const handleClose = useCallback(() => {
        if (!saving) onClose();
    }, [saving, onClose]);

    return (
        <Dialog
            open={open}
            onClose={handleClose}
            slotProps={{
                paper: {
                    component: 'form',
                    sx: { width: 400 },
                    onSubmit: hookForm.handleSubmit(handleSubmit)
                }

            }}
            sx={{
                "& .MuiDialog-container": {
                    "& .MuiPaper-root": {
                        width: "100%",
                        maxWidth: "45rem",
                    },
                },
            }}>

            <DialogTitle>
                Relatório de Rateio Geral
            </DialogTitle>

            <DialogContent>
                <ModalLoading loading={saving}  >
                    <FormContainer title={"Informações"} arrangement="fullRow">
                        <>
                            <div className="grid grid-cols-2 gap-4 mt-3">

                                <DefaultFormInput
                                    name="numeroConta"
                                    label="N° da conta"
                                    readOnly={false}
                                    reactHookForm={hookForm}
                                />

                                <DefaultFormMoneyInput
                                    name="valorPago"
                                    label="Valor Pago"
                                    reactHookForm={hookForm}
                                />

                            </div>
                            <DefaultFormMultiAutocomplete
                                name='centroCustos'
                                label='Centro de Custos'
                                options={ddlCentroCusto}
                                getOptionKey={x => x.id}
                                getOptionLabel={x => x.title}
                                reactHookForm={hookForm}

                            />

                            <DefaultFormMultiAutocomplete
                                name='fornecedores'
                                label='Fornecedores'
                                options={ddlFornecedor}
                                getOptionKey={x => x.id}
                                getOptionLabel={x => x.title}
                                reactHookForm={hookForm} />

                            <DefaultFormMultiAutocomplete
                                name='ordemCompras'
                                label='Ordens de Compra'
                                options={ddlOrdemCompra}
                                getOptionKey={x => x.id}
                                getOptionLabel={x => x.title}
                                reactHookForm={hookForm} />

                            <DefaultFormMultiAutocomplete
                                name='medicoes'
                                label='Medições'
                                options={ddlNroMedicao}
                                getOptionKey={x => x.id}
                                getOptionLabel={x => x.title}
                                reactHookForm={hookForm} />

                            <DefaultFormMultiAutocomplete
                                name='funcionarios'
                                label='Funcionários'
                                options={ddlFuncionario}
                                getOptionKey={x => x.id}
                                getOptionLabel={x => x.title}
                                reactHookForm={hookForm} />


                            <div className="grid grid-cols-2 gap-4 mb-3">
                                <FormDatePickerInput
                                    name="dataDe"
                                    label="Pago De"
                                    reactHookForm={hookForm}
                                />

                                <FormDatePickerInput
                                    name="dataAte"
                                    label="Pago Até"
                                    reactHookForm={hookForm}
                                />
                            </div>

                        </>
                    </FormContainer>

                </ModalLoading>
            </DialogContent>

            <DialogActions>
                <div className="w-full h-full items-center justify-end flex px-4 pb-4 gap-4">

                    <div className="flex flex-row gap-2">
                        <div className="p-2 hover:bg-hover-light-gray cursor-pointer dark:hover:bg-border-dark-gray rounded-[4px] font-semibold">
                            <h1
                                className="text-sm text-black dark:text-white"
                                onClick={handleClose}>
                                Cancelar
                            </h1>
                        </div>

                        <div className="w-[120px]">
                            {saving ? (
                                <PrimaryButton
                                    buttonColor="primary-green">
                                    Salvando...
                                </PrimaryButton>
                            ) : (
                                <PrimaryButton
                                    type="submit"
                                    buttonColor="primary-green">
                                    Gerar
                                </PrimaryButton>
                            )}
                        </div>
                    </div>
                </div>
            </DialogActions>

        </Dialog>
    );
}